import type { ChangeEvent, Dispatch, RefObject, SetStateAction } from 'react'
import { Tabs, TabButton } from '@runtime/components/ui/Tabs'
import styles from '@runtime/features/profile/layout/ProfileSidebar.module.scss'
import type { UserProfile } from '@runtime/features/profile/model/profileTypes'
import type { I18nT } from '@runtime/core/i18n/i18n'

type ProfileSidebarProps = {
  tab: string
  isAuthed: boolean
  setTab: Dispatch<SetStateAction<string>>
  t: I18nT
  user: UserProfile | null
  avatarUrl: string | null
  initial: string
  rating: number
  totalGames: number
  vsAI: number
  puzzlePlayed: number
  avatarFileInputRef: RefObject<HTMLInputElement | null>
  onAvatarFileChange: (event: ChangeEvent<HTMLInputElement>) => void
  onAvatarClick: () => void
}

export default function ProfileSidebar({
  tab,
  isAuthed,
  setTab,
  t,
  user,
  avatarUrl,
  initial,
  rating,
  totalGames,
  vsAI,
  puzzlePlayed,
  avatarFileInputRef,
  onAvatarFileChange,
  onAvatarClick,
}: ProfileSidebarProps) {
  return (
    <div className={styles.loginLeft}>
      <Tabs variant="strategy" className={styles.loginTabs}>
        {isAuthed ? (
          <>
            <TabButton
              className={styles.loginTab}
              active={tab === 'profile'}
              onClick={() => setTab('profile')}
            >
              {t('profile.tabs.profile')}
            </TabButton>
            <TabButton
              className={styles.loginTab}
              active={tab === 'history'}
              onClick={() => setTab('history')}
            >
              {t('profile.tabs.history')}
            </TabButton>
          </>
        ) : (
          <>
            <TabButton
              className={styles.loginTab}
              active={tab === 'login'}
              onClick={() => setTab('login')}
            >
              {t('profile.tabs.login')}
            </TabButton>
            <TabButton
              className={styles.loginTab}
              active={tab === 'register'}
              onClick={() => setTab('register')}
            >
              {t('profile.tabs.register')}
            </TabButton>
          </>
        )}
      </Tabs>

      <div className={styles.loginHeaderCard}>
        <button
          type="button"
          className={styles.loginAvatarBig}
          onClick={onAvatarClick}
          aria-disabled={!isAuthed}
          title={t('profile.avatarTooltip')}
          style={
            avatarUrl
              ? {
                  backgroundImage: `url(${avatarUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined
          }
        >
          {!avatarUrl && (
            <span className={styles.loginAvatarIcon}>{initial}</span>
          )}
        </button>

        <input
          ref={avatarFileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={onAvatarFileChange}
        />

        <div className={styles.loginHeaderName}>
          {user?.pseudo || t('profile.guest')}
        </div>

        {user?.email && (
          <div className={styles.loginHeaderEmail}>{user.email}</div>
        )}

        <span
          className={styles.loginRatingTag}
          title={t('profile.ratingTitle')}
        >
          Elo {rating}
        </span>

        <div className={styles.loginMiniStats}>
          <div className={styles.loginMiniStat}>
            <div className={styles.loginMiniStatLabel}>
              {t('profile.miniStats.total')}
            </div>
            <div className={styles.loginMiniStatValue}>{totalGames}</div>
          </div>
          <div className={styles.loginMiniStat}>
            <div className={styles.loginMiniStatLabel}>
              {t('profile.miniStats.vs-ai')}
            </div>
            <div
              className={styles.loginMiniStatValue}
              data-testid="profile-vs-ai-count"
            >
              {vsAI}
            </div>
          </div>
          <div className={styles.loginMiniStat}>
            <div className={styles.loginMiniStatLabel}>
              {t('profile.miniStats.puzzle')}
            </div>
            <div className={styles.loginMiniStatValue}>{puzzlePlayed}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
